import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import { get, post, put, del as apiDelete } from "../../services/api";
import styles from "./PTMAdminStyles.module.css"; // CSS Module import
import BookedAssessments from "../assessment/BookedAssessments";

function PtmAdminDashboard() {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      ptmTitle: "",
      ptmDescription: "",
      ptmDate: new Date().toISOString().split("T")[0],
      startTime: "09:00",
      endTime: "15:00",
      slotDuration: { value: 15, label: "15 minutes" },
      classCategory: null,
      classes: [],
      sections: [],
      teachingStaff: [],
      students: [],
    },
  });

  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");
  const centerId = localStorage.getItem("centerId");

  const [activeTab, setActiveTab] = useState("create");
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [teacherStats, setTeacherStats] = useState([]);

  const [classCategoriesData, setClassCategoriesData] = useState([]);
  const [classesData, setClassesData] = useState([]);
  const [sectionsData, setSectionsData] = useState([]);
  const [teachersData, setTeachersData] = useState([]);
  const [studentsData, setStudentsData] = useState([]);

  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showCalendar, setShowCalendar] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const CATEGORY_OPTIONS = [
    { id: 14, name: "Sr. Boys" },
    { id: 15, name: "Sr. Girls" },
  ];

  const SLOT_DURATION_OPTIONS = [
    { value: 5, label: "5 minutes" },
    { value: 10, label: "10 minutes" },
    { value: 15, label: "15 minutes" },
    { value: 20, label: "20 minutes" },
    { value: 30, label: "30 minutes" },
  ];

  useEffect(() => {
    fetchTeachers();
    fetchEvents();
  }, []);

  const fetchTeachers = async () => {
    try {
      const response = await get(
        `UserAuthentication/GetUsersByCenterIdAndRoleId?centerId=${centerId}&roleId=7`,
        { headers }
      );
      setTeachersData(response || []);
    } catch (error) {
      console.error("Error fetching teachers:", error);
    }
  };

  const fetchClasses = async (categoryId) => {
    try {
      if (!categoryId) {
        setClassesData([]);
        return [];
      }
      const response = await get(
        `CenterClass/GetClassCenterByCenterId?id=${centerId}`,
        { headers }
      );
      const classData = response || [];
      setClassesData(classData);
      return classData;
    } catch (error) {
      console.error("Error fetching classes:", error);
      setClassesData([]);
      return [];
    }
  };

  const fetchSections = async (classIds) => {
    try {
      if (!classIds || classIds.length === 0) {
        setSectionsData([]);
        return [];
      }
      const response = await get(
        `ClassSection/GetAllSectionByCenterClassIdAndCenterIds?centerClassId=${classIds.join(
          ","
        )}&centerId=${centerId || 3}`,
        { headers }
      );
      const sectionData = response || [];
      setSectionsData(sectionData);
      return sectionData;
    } catch (error) {
      console.error("Error fetching sections:", error);
      setSectionsData([]);
      return [];
    }
  };

  const fetchStudents = async (classSectionIds) => {
    try {
      if (!classSectionIds || classSectionIds.length === 0) {
        setStudentsData([]);
        return [];
      }

      const studentList = [];
      for (const sectionId of classSectionIds) {
        const response = await get(
          `Student/GetStudentByClassSectionIdAsync?classSectionId=${sectionId}&userId=0`,
          { headers }
        );
        if (response && response.length > 0) {
          studentList.push(...response);
        }
      }

      const uniqueStudents = Array.from(
        new Map(studentList.map((item) => [item.id, item])).values()
      );
      setStudentsData(uniqueStudents);
      return uniqueStudents;
    } catch (error) {
      console.error("Error fetching students:", error);
      setStudentsData([]);
      return [];
    }
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const data = await get(`PTM/Admin/GetEventsByCenter/${centerId}`, {
        headers,
      });
      setEvents(data || []);
    } catch (error) {
      console.error("Error fetching events:", error);
      showError("Failed to fetch events");
    } finally {
      setLoading(false);
    }
  };

  const fetchTeacherStats = async (eventId) => {
    try {
      setLoading(true);
      const data = await get(`PTM/Admin/GetTeacherStats/${eventId}`, {
        headers,
      });
      setTeacherStats(data || []);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (option) => {
    setValue("classCategory", option);
    setValue("classes", []);
    setValue("sections", []);
    setValue("students", []);
    if (option?.value) {
      fetchClasses(option.value);
    }
  };

  const handleClassesChange = (options) => {
    setValue("classes", options || []);
    setValue("sections", []);
    setValue("students", []);
    if (options && options.length > 0) {
      fetchSections(options.map((o) => o.value));
    }
  };

  const handleSectionsChange = (options) => {
    setValue("sections", options || []);
    setValue("students", []);
    if (options && options.length > 0) {
      fetchStudents(options.map((o) => o.value));
    }
  };

  const onSubmit = async (formData) => {
    try {
      setLoading(true);

      const payload = {
        title: formData.ptmTitle,
        description: formData.ptmDescription,
        eventDate: new Date(formData.ptmDate),
        startTime: formData.startTime,
        endTime: formData.endTime,
        slotDurationMinutes: formData.slotDuration.value,
        classCategoryId: formData.classCategory?.value,
        centerClassIds: formData.classes?.map((c) => c.value) || [],
        classSectionIds: formData.sections?.map((s) => s.value) || [],
        teacherIds: formData.teachingStaff?.map((t) => t.value) || [],
        studentIds: formData.students?.map((s) => s.value) || [],
        centerId: parseInt(centerId),
        userId: parseInt(userId),
      };

      if (editingId) {
        await put(`PTM/Admin/UpdateEvent/${editingId}`, payload, { headers });
        showSuccess("PTM Event updated successfully!");
        setEditingId(null);
      } else {
        await post("PTM/Admin/CreateEvent", payload, { headers });
        showSuccess("PTM Event created successfully!");
      }

      reset();
      fetchEvents();
      setActiveTab("view");
    } catch (error) {
      showError(error.response?.data?.message || "Error creating event");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (window.confirm("Are you sure you want to delete this event?")) {
      try {
        await apiDelete(`PTM/Admin/DeleteEvent/${eventId}`, { headers });
        showSuccess("Event deleted successfully");
        fetchEvents();
      } catch (error) {
        showError("Error deleting event");
      }
    }
  };

  // ✅ FIXED: handleEditEvent - Now properly populates all dropdowns
  const handleEditEvent = async (event) => {
    setEditingId(event.id);
    setActiveTab("create");

    // Reset any previous data
    setClassesData([]);
    setSectionsData([]);
    setStudentsData([]);

    // 1. Basic fields
    setValue("ptmTitle", event.title);
    setValue("ptmDescription", event.description || "");
    setValue("ptmDate", event.eventDate.split("T")[0]);
    setValue("startTime", event.startTime.slice(0, 5));
    setValue("endTime", event.endTime.slice(0, 5));
    setValue("slotDuration", {
      value: event.slotDurationMinutes,
      label: `${event.slotDurationMinutes} minutes`,
    });

    // 2. Set Category
    const categoryOption = CATEGORY_OPTIONS.find(
      (cat) => cat.id === event.classCategoryId
    );
    if (!categoryOption) return;

    const categorySelectValue = {
      value: categoryOption.id,
      label: categoryOption.name,
    };
    setValue("classCategory", categorySelectValue);

    // 3. Fetch and SET Classes (both state and value)
    if (event.centerClassIds?.length > 0) {
      const fetchedClasses = await fetchClasses(categoryOption.id);
      // CRITICAL: Manually set the state so dropdown has options
      setClassesData(fetchedClasses);

      const selectedClassOptions = fetchedClasses
        .filter((cls) => event.centerClassIds.includes(cls.id))
        .map((cls) => ({
          value: cls.id,
          label: cls.name,
        }));
      setValue("classes", selectedClassOptions);
    } else {
      setClassesData([]);
      setValue("classes", []);
    }

    // 4. Fetch and SET Sections
    if (event.classSectionIds?.length > 0 && event.centerClassIds?.length > 0) {
      const fetchedSections = await fetchSections(event.centerClassIds);

      // CRITICAL: Set state so dropdown shows options
      setSectionsData(fetchedSections);

      const selectedSectionOptions = fetchedSections
        .filter((sec) => event.classSectionIds.includes(sec.id))
        .map((sec) => ({
          value: sec.id,
          label: sec.name,
        }));

      setValue("sections", selectedSectionOptions);
    } else {
      setSectionsData([]);
      setValue("sections", []);
    }

    // 5. Teachers (already loaded on mount)
    if (event.teacherIds?.length > 0) {
      const selectedTeacherOptions = teachersData
        .filter((t) => event.teacherIds.includes(t.id))
        .map((t) => ({
          value: t.id,
          label: `${t.firstName} ${t.lastName}`.trim(),
        }));

      setValue("teachingStaff", selectedTeacherOptions);
    } else {
      setValue("teachingStaff", []);
    }

    // Students (if any)
    if (event.studentIds?.length > 0 && event.classSectionIds?.length > 0) {
      const fetchedStudents = await fetchStudents(event.classSectionIds);
      setStudentsData(fetchedStudents);

      const selectedStudentOptions = fetchedStudents
        .filter((s) => event.studentIds.includes(s.id))
        .map((s) => ({
          value: s.id,
          label: s.studentName || "Unknown Student",
        }));

      setValue("students", selectedStudentOptions);
    } else {
      setStudentsData([]);
      setValue("students", []);
    }
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    fetchTeacherStats(event.id);
  };

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const showError = (msg) => {
    setErrorMessage(msg);
    setTimeout(() => setErrorMessage(""), 3000);
  };

  return (
    <div className={styles.ptmAdminContainer}>
      {/* Header */}
      {/* <div className={styles.ptmHeader}>
        <div className={styles.ptmHeaderContent}>
          <h1>PTM Management Dashboard</h1>
          <p>Schedule and manage Parent-Teacher Meetings</p>
        </div>
      </div> */}

      {/* Alerts */}
      {successMessage && (
        <div className={`${styles.alert} ${styles.alertSuccess}`}>
          <strong>✓ Success!</strong> {successMessage}
          <button
            className={styles.btnClose}
            onClick={() => setSuccessMessage("")}
          ></button>
        </div>
      )}

      {errorMessage && (
        <div className={`${styles.alert} ${styles.alertDanger}`}>
          <strong>✗ Error!</strong> {errorMessage}
          <button
            className={styles.btnClose}
            onClick={() => setErrorMessage("")}
          ></button>
        </div>
      )}

      <div className={styles.ptmContainer}>
        {/* Tabs */}
        <div className={styles.ptmTabs}>
          <button
            className={`${styles.tabBtn} ${
              activeTab === "create" ? styles.active : ""
            }`}
            onClick={() => {
              setActiveTab("create");
              setEditingId(null);
              reset();
            }}
          >
            ➕ Create Event
          </button>
          <button
            className={`${styles.tabBtn} ${
              activeTab === "view" ? styles.active : ""
            }`}
            onClick={() => setActiveTab("view")}
          >
            📋 View Events
          </button>
        </div>

        {/* CREATE TAB */}
        {activeTab === "create" && (
          <div className={styles.ptmFormContainer}>
            <form onSubmit={handleSubmit(onSubmit)}>
              {/* Section 1: Meeting Details */}
              <section className={styles.ptmSection}>
                <h2 className={styles.sectionTitle}>📅 Meeting Details</h2>

                {/* Title */}
                <div className={styles.formGroupFull}>
                  <label className={styles.formLabel}>
                    Event Title <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    className={`${styles.formInput} ${styles.formInputLg} ${
                      errors.ptmTitle ? styles.error : ""
                    }`}
                    placeholder="e.g., First Term PTM 2025"
                    {...register("ptmTitle", {
                      required: "Title is required",
                    })}
                  />
                  {errors.ptmTitle && (
                    <span className={styles.errorText}>
                      {errors.ptmTitle.message}
                    </span>
                  )}
                </div>

                {/* Description */}
                <div className={styles.formGroupFull}>
                  <label className={styles.formLabel}>Description</label>
                  <textarea
                    className={styles.formTextarea}
                    rows="4"
                    placeholder="Detailed description for staff and parents..."
                    {...register("ptmDescription")}
                  ></textarea>
                </div>

                <hr className={styles.sectionDivider} />

                {/* Date & Time */}
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      Event Date <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="date"
                      className={`${styles.formInput} ${
                        errors.ptmDate ? styles.error : ""
                      }`}
                      {...register("ptmDate", {
                        required: "Date is required",
                      })}
                    />
                    {errors.ptmDate && (
                      <span className={styles.errorText}>
                        {errors.ptmDate.message}
                      </span>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      Start Time <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="time"
                      className={`${styles.formInput} ${
                        errors.startTime ? styles.error : ""
                      }`}
                      {...register("startTime", {
                        required: "Start time is required",
                      })}
                    />
                    {errors.startTime && (
                      <span className={styles.errorText}>
                        {errors.startTime.message}
                      </span>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      End Time <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="time"
                      className={`${styles.formInput} ${
                        errors.endTime ? styles.error : ""
                      }`}
                      {...register("endTime", {
                        required: "End time is required",
                      })}
                    />
                    {errors.endTime && (
                      <span className={styles.errorText}>
                        {errors.endTime.message}
                      </span>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      Slot Duration <span className={styles.required}>*</span>
                    </label>
                    <Controller
                      name="slotDuration"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={SLOT_DURATION_OPTIONS}
                          className="ptm-select"
                          classNamePrefix="react-select"
                          isSearchable={false}
                        />
                      )}
                    />
                  </div>
                </div>
              </section>

              {/* Section 2: Category, Class, Section */}
              <section className={styles.ptmSection}>
                <h2 className={styles.sectionTitle}>🎓 Class Details</h2>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      Category <span className={styles.required}>*</span>
                    </label>
                    <Controller
                      name="classCategory"
                      control={control}
                      rules={{ required: "Category is required" }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={CATEGORY_OPTIONS.map((cat) => ({
                            value: cat.id,
                            label: cat.name,
                          }))}
                          onChange={handleCategoryChange}
                          className="ptm-select"
                          classNamePrefix="react-select"
                          placeholder="Select category"
                          isClearable
                          isSearchable={false}
                        />
                      )}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      Classes <span className={styles.required}>*</span>
                    </label>
                    <Controller
                      name="classes"
                      control={control}
                      rules={{ required: "Select at least one class" }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          isMulti
                          options={classesData.map((c) => ({
                            value: c.id,
                            label: c.name,
                          }))}
                          onChange={(selectedOptions) => {
                            // Let RHF handle the value
                            field.onChange(selectedOptions || []);

                            // Side effects: clear dependent fields and fetch new sections
                            setValue("sections", []);
                            setValue("students", []);

                            if (selectedOptions && selectedOptions.length > 0) {
                              const classIds = selectedOptions.map(
                                (o) => o.value
                              );
                              fetchSections(classIds);
                            } else {
                              setSectionsData([]);
                              setStudentsData([]);
                            }
                          }}
                          className="ptm-select"
                          classNamePrefix="react-select"
                          placeholder="Select classes"
                          closeMenuOnSelect={false}
                          isSearchable={false}
                        />
                      )}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      Sections <span className={styles.required}>*</span>
                    </label>
                    <Controller
                      name="sections"
                      control={control}
                      rules={{ required: "Select at least one section" }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          isMulti
                          options={sectionsData.map((s) => ({
                            value: s.id,
                            label: s.name,
                          }))}
                          onChange={(selectedOptions) => {
                            field.onChange(selectedOptions || []);

                            setValue("students", []);

                            if (selectedOptions && selectedOptions.length > 0) {
                              const sectionIds = selectedOptions.map(
                                (o) => o.value
                              );
                              fetchStudents(sectionIds);
                            } else {
                              setStudentsData([]);
                            }
                          }}
                          className="ptm-select"
                          classNamePrefix="react-select"
                          placeholder="Select sections"
                          closeMenuOnSelect={false}
                          isSearchable={false}
                        />
                      )}
                    />
                  </div>
                </div>
              </section>

              {/* Section 3: Teachers */}
              <section className={styles.ptmSection}>
                <h2 className={styles.sectionTitle}>👨‍🏫 Teaching Staff</h2>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                      Select Teachers <span className={styles.required}>*</span>
                    </label>
                    <Controller
                      name="teachingStaff"
                      control={control}
                      rules={{ required: "Select at least one teacher" }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          isMulti
                          options={teachersData.map((t) => ({
                            value: t.id,
                            label: `${t.firstName} ${t.lastName}`,
                            data: t,
                          }))}
                          onChange={(options) => {
                            field.onChange(options);
                            // Show calendar popup when teacher selected
                            if (options && options.length > 0) {
                              //setShowCalendar(true);
                            }
                          }}
                          className="ptm-select"
                          classNamePrefix="react-select"
                          placeholder="Select teachers"
                          closeMenuOnSelect={false}
                          isSearchable={true}
                        />
                      )}
                    />
                    <small className={styles.helpText}>
                      💡 Click to view teacher availability
                    </small>
                  </div>
                </div>
              </section>

              {/* Submit */}
              <div className={styles.formActions}>
                <button
                  type="submit"
                  className={`${styles.btn} ${styles.btnPrimary} ${styles.btnLg}`}
                  disabled={loading}
                >
                  {loading
                    ? "Processing..."
                    : editingId
                    ? "Update Event"
                    : "Create Event"}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* VIEW TAB */}
        {activeTab === "view" && (
          <div className={styles.ptmViewContainer}>
            {/* Events List */}
            <section className={styles.ptmSection}>
              <h2 className={styles.sectionTitle}>📋 All PTM Events</h2>

              {loading ? (
                <div className={styles.loadingSpinner}>
                  <div className={styles.spinner}></div>
                  <p>Loading events...</p>
                </div>
              ) : events.length === 0 ? (
                <div className={styles.emptyState}>
                  <p>No events found. Create one to get started!</p>
                </div>
              ) : (
                <div className={styles.eventsGrid}>
                  {events.map((event) => (
                    <div key={event.id} className={styles.eventCard}>
                      <div className={styles.eventCardHeader}>
                        <h3>{event.title}</h3>
                        <span className={styles.eventDate}>
                          📅 {new Date(event.eventDate).toLocaleDateString()}
                        </span>
                      </div>

                      <div className={styles.eventStats}>
                        <div className={styles.stat}>
                          <span className={styles.statValue}>
                            {event.totalTeachers}
                          </span>
                          <span className={styles.statLabel}>Teachers</span>
                        </div>
                        <div className={styles.stat}>
                          <span className={styles.statValue}>
                            {event.totalSlots}
                          </span>
                          <span className={styles.statLabel}>Slots</span>
                        </div>
                        <div className={styles.stat}>
                          <span
                            className={`${styles.statValue} ${styles.statValueSuccess}`}
                          >
                            {event.bookedSlots}
                          </span>
                          <span className={styles.statLabel}>Booked</span>
                        </div>
                      </div>

                      <div className={styles.eventActions}>
                        <button
                          className={`${styles.btn} ${styles.btnSm} ${styles.btnPrimary}`}
                          onClick={() => handleSelectEvent(event)}
                        >
                          Details
                        </button>
                        <button
                          className={`${styles.btn} ${styles.btnSm} ${styles.btnWarning}`}
                          onClick={() => handleEditEvent(event)}
                        >
                          Edit
                        </button>
                        <button
                          className={`${styles.btn} ${styles.btnSm} ${styles.btnDanger}`}
                          onClick={() => handleDeleteEvent(event.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Event Details */}
            {selectedEvent && teacherStats.length > 0 && (
              <section className={styles.ptmSection}>
                <h2 className={styles.sectionTitle}>📊 Teacher Statistics</h2>

                <div className={styles.tableResponsive}>
                  <table className={styles.statsTable}>
                    <thead>
                      <tr>
                        <th>Teacher Name</th>
                        <th>Class/Section</th>
                        <th className={styles.textCenter}>Total</th>
                        <th className={styles.textCenter}>Booked</th>
                        <th className={styles.textCenter}>Available</th>
                        <th className={styles.textCenter}>%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teacherStats.map((stat) => (
                        <tr key={stat.teacherId}>
                          <td>
                            <strong>{stat.teacherName}</strong>
                          </td>
                          <td>{stat.classSectionName}</td>
                          <td className={styles.textCenter}>
                            {stat.totalSlots}
                          </td>
                          <td className={styles.textCenter}>
                            <span
                              className={`${styles.badge} ${styles.badgeBgSuccess}`}
                            >
                              {stat.bookedSlots}
                            </span>
                          </td>
                          <td className={styles.textCenter}>
                            <span
                              className={`${styles.badge} ${styles.badgeBgSecondary}`}
                            >
                              {stat.availableSlots}
                            </span>
                          </td>
                          <td className={styles.textCenter}>
                            <div className={styles.progressMini}>
                              <div
                                className={styles.progressBar}
                                style={{
                                  width: `${stat.bookingPercentage}%`,
                                }}
                              ></div>
                            </div>
                            {Math.round(stat.bookingPercentage)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </div>
        )}
      </div>

      {/* Teacher Calendar Modal - Using BookedAssessments - POSITIONED ON RIGHT */}
      {showCalendar && (
        <div
          className={styles.modalOverlay}
          onClick={() => setShowCalendar(false)}
        >
          <div
            className={`${styles.modalContent} ${styles.modalLarge}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h3>📅 Teacher Availability</h3>
              <button
                className={styles.modalClose}
                onClick={() => setShowCalendar(false)}
              >
                ✕
              </button>
            </div>
            <div className={styles.modalBodyZoomed}>
              <div className={styles.bookedAssessmentsZoom}>
                <BookedAssessments
                  heading="Teacher Availability"
                  onBack={() => setShowCalendar(false)}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PtmAdminDashboard;
